import React, { useState, useContext, useEffect } from 'react';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { useIndexedDB } from '@thd-olt-global/thd-storage-utils';
import { StickyWithHeaderObserver } from '@thd-olt-component-react/sticky';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { AssistantActionButton } from './components/AssistantActionButton';
import { ResponsiveAssistant } from './components/ResponsiveAssistant';
import { IFrameManager } from './components/IFrameManager';
import { AssistantHeader } from './components/AssistantHeader';
import {
  getPageType,
  getSessionId,
  getIsCompatiblePage,
  nrTrack
} from './utils/assistant-utils';
import { useStickyElementOffset } from './hooks/useStickyElementOffset';
import './app.scss';
import { NR } from './components/MagicApronAssistant/constants';

export const App = () => {
  const { channel, isConsumerApp, customer } = useContext(ExperienceContext);
  const {
    getByKey: iDBGetByKey_session,
    update: iDBUpdate_session,
  } = useIndexedDB('sessions', 'assistantBotDB');

  const currentPageType = getPageType();
  const isMobile = channel === 'mobile';

  const [drawerState, setDrawerState] = useState('CLOSED');
  const [hasSessionInteractedWithFab, setHasSessionInteractedWithFab] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const offsetMargin = useStickyElementOffset();

  const fsDTEnabled = useConfigService('magic-apron-assistant-dt-enabled');
  const fsMWEnabled = useConfigService('magic-apron-assistant-mw-enabled');
  const fsAPPEnabled = useConfigService('magic-apron-assistant-app-enabled');
  const isAssistantCompatiblePage = getIsCompatiblePage();

  const getSessionAndSetDrawerState = () => {
    iDBGetByKey_session(getSessionId())
      .then((session) => {
        if (!session) return { shouldOpen: ['PIP', 'PLP'].includes(currentPageType) };
        return { shouldOpen: session?.shouldOpenDrawer && ['PIP', 'PLP'].includes(currentPageType) };
      })
      .catch(() => {
        return { shouldOpen: ['PIP', 'PLP'].includes(currentPageType) };
      })
      .then((res) => {
        if (res.shouldOpen) {
          nrTrack(NR.OPEN, { openType: isMobile ? 'DEFAULT_MINIMIZED' : 'DEFAULT_OPEN' });
          setDrawerState(isMobile ? 'MINIMIZED' : 'OPEN');
        }
        setIsInitialized(true);
      });
  };

  useEffect(() => {
    // Inverse of the App null checks to track if we intended for compoenent to load or not.
    const fsShouldRender = (
      (fsDTEnabled && !isMobile)
      || (fsMWEnabled && isMobile && !isConsumerApp)
      || (fsAPPEnabled && isMobile && isConsumerApp)
    );

    const additionalChecks = !(
      !isAssistantCompatiblePage || customer?.type !== 'b2c' || typeof BroadcastChannel === 'undefined'
    );
    const renderIntent = fsShouldRender && additionalChecks;
    nrTrack(NR.LOAD, { renderIntent });

    // Listen for communication from iframe
    const maaBroadcastChannel = new BroadcastChannel('magic-apron-assistant');
    maaBroadcastChannel.onmessage = ({ data: { type, data } }) => {
      if (type === 'MAA_ASK_A_QUESTION') {
        setDrawerState('OPEN');
      }
      if (type === 'MAA_USER_QUESTION') {
        setDrawerState('OPEN');
        nrTrack(NR.OPEN, { openType: 'QUESTION_OPEN' });
        window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.submit-click', {
          sessionId: getSessionId(),
          question: `${data.messageOrigin}: ${data.userQuestion}`,
        });
      }
      if (type === 'MAA_PRODUCT_POD_CLICK') {
        window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.product-pod-click', data);
      }
      if (type === 'MAA_PRODUCT_POD_ATC_CLICK') {
        window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.product-pod-atc-click', data);
      }
    };

    // Determine if drawer should auto open on page load
    const scheduleAutoOpen = () => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(() => {
          getSessionAndSetDrawerState();
        }, { timeout: 1000 });
      } else {
        setTimeout(() => {
          getSessionAndSetDrawerState();
        }, isMobile ? 300 : 0);
      }
    };

    setTimeout(scheduleAutoOpen, 0);
  }, []);

  const updateSession = async (sessionUpdates) => {
    let retrievedSession = await iDBGetByKey_session(getSessionId());
    retrievedSession = {
      ...retrievedSession,
      ...sessionUpdates
    };

    await iDBUpdate_session(retrievedSession).then(() => {
      setHasSessionInteractedWithFab(retrievedSession.hasSessionInteractedWithFab);
    });
  };

  const onOpen = () => {
    nrTrack(NR.OPEN, { openType: 'FAB_OPEN' });
    if (!hasSessionInteractedWithFab) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.fab-click', {
        sessionID: getSessionId(),
      });
    }
    setDrawerState('OPEN');
    updateSession({
      shouldOpenDrawer: true,
      hasSessionInteractedWithFab: true,
    })
      .catch(() => {});
  };

  const onClose = () => {
    nrTrack(NR.CLOSE, { openType: 'FAB_CLOSE' });
    setDrawerState('CLOSED');
    updateSession({
      shouldOpenDrawer: false,
    })
      .catch(() => {});
  };

  if (!(
    (fsDTEnabled && !isMobile)
    || (fsMWEnabled && isMobile && !isConsumerApp)
    || (fsAPPEnabled && isMobile && isConsumerApp))
  ) return null;

  if (!isAssistantCompatiblePage || customer?.type !== 'b2c'
      || typeof BroadcastChannel === 'undefined' || !isInitialized) return null;

  const renderActionButton = () => {
    if (drawerState !== 'CLOSED') return null;

    if (isMobile) {
      return (
        <StickyWithHeaderObserver
          className="sui-w-full"
          position="bottom"
          stickyByDefault
          visibleByDefault
          offsetMargin={offsetMargin}
        >
          <ImpressionProvider
            data={{
              id: '00DEFVALMA00',
              component: 'MagicApronAssistant',
              name: 'MagicApronLoader',
              type: 'content'
            }}
          >
            <AssistantActionButton onClick={onOpen} isMobile />
          </ImpressionProvider>
        </StickyWithHeaderObserver>
      );
    }

    return (
      <ImpressionProvider
        data={{
          id: '00DEFVALMA00',
          component: 'MagicApronAssistant',
          name: 'MagicApronLoader',
          type: 'content'
        }}
      >
        <AssistantActionButton onClick={onOpen} />
      </ImpressionProvider>
    );
  };

  return (
    <>
      {renderActionButton()}
      {drawerState !== 'CLOSED' && (
        <ResponsiveAssistant drawerState={drawerState} setDrawerState={setDrawerState} offsetMargin={offsetMargin}>
          <IFrameManager>
            {drawerState === 'OPEN'
              && <AssistantHeader onClose={onClose} />}
          </IFrameManager>
        </ResponsiveAssistant>
      )}
    </>
  );
};
